import {
    AfterViewInit,
    ChangeDetectorRef,
    ContentChildren,
    Directive,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    NgZone,
    OnDestroy,
    Output,
    QueryList,
    Renderer2,
} from "@angular/core";
import { S25Util } from "../../util/s25-util";
import { Debounce } from "../../decorators/debounce.decorator";
import { S25DndSortableItemDirective } from "./s25.dnd.sortable.item.directive";
import { Bind } from "../../decorators/bind.decorator";

export interface SortableEvent {
    "s25-dnd-sortable-item--index": number;
    "s25-dnd-sortable-item--belongsTo"?: any[];
    [key: string]: any;
}

@Directive({
    selector: "[s25-ng-dnd-sortable]",
    standalone: false,
})
export class S25DndSortableDirective implements AfterViewInit, OnDestroy {
    @Input() items: any[] = [];
    @Input()
    @HostBinding("class.sortable")
    sortable = true; // Set to false to disable sorting

    @Output() orderChanged = new EventEmitter();

    @ContentChildren(S25DndSortableItemDirective) itemDirectives: QueryList<S25DndSortableItemDirective>;

    listeners: ReturnType<Renderer2["listen"]>[] = [];
    destroyed: boolean = false;

    constructor(
        private elementRef: ElementRef,
        private changeDetector: ChangeDetectorRef,
        private zone: NgZone,
        private renderer: Renderer2,
    ) {}

    ngAfterViewInit() {
        this.setEventListeners();
    }

    setEventListeners() {
        const elem = this.elementRef.nativeElement;
        const dragEvents = ["dragstart", "dragenter", "dragend", "dragover"];
        const keyEvents = [
            "keydown.enter",
            "keydown.space",
            "keydown.arrowup",
            "keydown.arrowleft",
            "keydown.arrowdown",
            "keydown.arrowright",
            "keydown.tab",
            "keydown.shift.tab",
        ];
        this.zone.runOutsideAngular(() => {
            for (const event of dragEvents) {
                this.listeners.push(this.renderer.listen(elem, event, this.onDragEvent));
            }

            for (const event of keyEvents) {
                this.listeners.push(this.renderer.listen(elem, event, this.onKeyEvent));
            }
        });
    }

    ngOnDestroy() {
        this.removeEventListeners();
        this.destroyed = true;
    }

    removeEventListeners() {
        for (const listener of this.listeners) listener();
    }

    draggedIndex: number;
    @HostBinding("class.s25-dnd-sortable--dragging") dragging = false;
    @HostBinding("class.s25-dnd-sortable--dragging-keyboard") keyboardDragging = false;
    @HostBinding("attr.role") ariaRole = "list"; // Allow description

    @Bind
    onDragEvent(event: Event & SortableEvent) {
        if (!this.sortable || !this.belongsHere(event)) return; // Skip if event is not for this list
        switch (event.type) {
            case "dragstart":
                return this.startDrag(this.getIndex(event));
            case "dragenter":
                return this.attemptReorder(event, this.draggedIndex, this.getIndex(event));
            case "dragend":
                return this.cancelDrag();
            case "dragover": // Allow dropping if item is already moved in onDragEnter
                if (this.draggedIndex === this.getIndex(event)) event.preventDefault();
                return;
        }
    }

    @Bind
    onKeyEvent(event: KeyboardEvent & SortableEvent) {
        if (!this.sortable || !this.belongsHere(event)) return; // Skip if event is not for this list
        switch (event.key) {
            case "Enter":
            case " ":
                return this.toggleKeyboardDrag(event);
            case "ArrowUp":
            case "ArrowLeft":
                return this.keyboardIncrement(event, -1);
            case "ArrowDown":
            case "ArrowRight":
                return this.keyboardIncrement(event, 1);
            case "Tab":
                if (this.dragging) event.preventDefault();
                return;
        }
    }

    toggleKeyboardDrag(event: KeyboardEvent & SortableEvent) {
        if (this.dragging !== this.keyboardDragging) return; // Skip if mouse dragging
        if (!this.dragging) this.startDrag(this.getIndex(event));
        else {
            this.attemptReorder(event, this.draggedIndex, this.getIndex(event));
            this.cancelDrag();
        }
        this.keyboardDragging = this.dragging;
        event.preventDefault();
    }

    startDrag(index: number) {
        this.draggedIndex = index;
        this.dragging = true;
        this.announce(`Picked up item ${index + 1} of ${this.items.length}`, true);
    }

    cancelDrag() {
        this.dragging = false;
        this.keyboardDragging = false;
        this.announce(`Dropped item at position ${this.draggedIndex + 1} of ${this.items.length}`, true);
    }

    keyboardIncrement(event: any, direction: -1 | 1) {
        if (!this.keyboardDragging) return;
        this.attemptReorder(event, this.draggedIndex, this.draggedIndex + direction);
        event.preventDefault(); // Prevent scrolling
    }

    attemptReorder(event: Event & SortableEvent, from: number, to: number) {
        if (!this.dragging) return;
        if (from == null || to == null) return; // Skip if invalid indices
        if (event?.fromElement?.closest("[s25-ng-dnd-sortable-item]") === this.elementRef.nativeElement) return; // Ignore internal dragging
        // Clamp indices to bounds
        from = S25Util.clamp(from, 0, this.items.length - 1);
        to = S25Util.clamp(to, 0, this.items.length - 1);
        if (to === from) return; // No need to move if same
        // All is good
        this.reorderItem(event, from, to);
        event.stopImmediatePropagation();
    }

    @Debounce(100)
    reorderItem(event: any, from: number, to: number) {
        if (this.destroyed) return;
        const item = this.items.splice(from, 1)[0];
        this.items.splice(to, 0, item);
        this.draggedIndex = to;
        this.afterReorder(event);
    }

    afterReorder(event: any) {
        this.changeDetector.detectChanges();
        if (this.keyboardDragging) {
            const test = this.itemDirectives.toArray().find((item) => {
                return item.index === this.draggedIndex && (!item.belongsTo || item.belongsTo === this.items);
            });
            test.elementRef.nativeElement.focus(); // Focus is lost, so get it back
            test.elementRef.nativeElement.scrollIntoView();
        }
        this.orderChanged.emit(this.items); // when using signals, emit array so the signal can be updated
        this.announce(`Moved item to position ${this.draggedIndex + 1} of ${this.items.length}`, true);
    }

    getIndex(event: SortableEvent) {
        return event["s25-dnd-sortable-item--index"];
    }

    belongsHere(event: SortableEvent) {
        const belongsTo = event["s25-dnd-sortable-item--belongsTo"];
        const isItem = event["s25-dnd-sortable-item"];
        return isItem && (belongsTo == null || belongsTo === this.items);
    }

    announce(text: string, assertive?: boolean) {
        // Use aria-live to speak a string to screen readers
        const elem = document.createElement("div");
        elem.setAttribute("aria-live", assertive ? "assertive" : "polite");
        elem.style.position = "absolute";
        elem.style.opacity = "0";
        elem.innerText = text;
        this.elementRef.nativeElement.appendChild(elem);
        setTimeout(() => {
            this.elementRef.nativeElement.removeChild(elem);
        }, 1000);
    }
}
